import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { DynamicFormsService } from '@kinet-open-network/client/core/forms/util';
import { toast } from '@kinet-open-network/client/shared/ui';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, map, tap } from 'rxjs/operators';
import { ErrorHandlerActions } from './error-handler.actions';
import { selectError, selectValidationErrors } from './error-handler.selectors';

export const handleHttpError$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) => {
    return actions$.pipe(
      ofType(ErrorHandlerActions.throwError),
      concatLatestFrom(() => store.select(selectError)),
      map(([{ error }, currentError]) => {
        if (error.status !== 422 && currentError?.status === error.status) {
          return ErrorHandlerActions.noOp(); // Avoid duplicate errors
        }

        return ErrorHandlerActions.setError({ error });
      }),
    );
  },
  {
    functional: true,
  },
);

export const showErrorToast$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) => {
    return actions$.pipe(
      ofType(ErrorHandlerActions.setError),
      tap(({ error }) => {
        const msg = error.error?.message || error.message;
        const onClose = () => {
          store.dispatch(ErrorHandlerActions.clearError({ error }));
        };
        const errorCode = error.error?.errorCode;
        const errorCodeSuffix = errorCode ? `(kód chyby: ${errorCode})` : '';
        const getDetailMessage = (baseMsg: string) =>
          `${baseMsg} ${errorCodeSuffix}`.trim();

        switch (error.status) {
          case 0: {
            toast({
              severity: 'danger',
              summary: 'Chyba siete',
              detail: getDetailMessage(
                'Problém s pripojením k serveru. Skontrolujte internetové pripojenie.',
              ),
              closable: true,
              onClose,
            });
            break;
          }
          case 403: {
            toast({
              severity: 'danger',
              summary: 'Chyba oprávnenia',
              detail: getDetailMessage(
                `Nemáte oprávnenie na vykonanie tejto akcie.\n${msg}`,
              ),
              closable: true,
              onClose,
            });
            break;
          }
          case 401:
            // Unauthorized error is handled in auth interceptor
            break;
          case 422:
            // This is handled separately in setFormErrors$
            break;
          case 502:
          case 503: {
            toast({
              severity: 'danger',
              summary: `${error.status}: Chyba servera`,
              detail: getDetailMessage(
                `Server je momentálne nedostupný.\n${msg || 'Skúste to prosím neskôr.'}`,
              ),
              closable: true,
              onClose,
            });
            break;
          }
          case 504: {
            toast({
              severity: 'danger',
              summary: '504: Chyba servera',
              detail: getDetailMessage(
                `Server neodpovedal včas.\n${msg || 'Skúste to prosím neskôr.'}`,
              ),
              closable: true,
              onClose,
            });
            break;
          }
          default:
            toast({
              severity: 'danger',
              summary: `${error.status}: ${error.statusText}`,
              detail: msg
                ? getDetailMessage(msg)
                : errorCode
                  ? `Kód chyby: ${errorCode}`
                  : 'Neznáma chyba.',
              closable: true,
              onClose,
            });
            break;
        }
      }),
    );
  },
  { dispatch: false, functional: true },
);

export const setFormErrors$ = createEffect(
  (
    actions$ = inject(Actions),
    formsService = inject(DynamicFormsService),
    store = inject(Store),
  ) => {
    return actions$.pipe(
      ofType(ErrorHandlerActions.setError),
      filter(({ error }) => error.status === 422),
      concatLatestFrom(() => store.select(selectValidationErrors)),
      tap(([{ error }, errors]) => {
        if (errors) {
          formsService.setApiErrors(errors);

          const errCount = Object.keys(errors).length;
          let errCountText = `${errCount} chýb`;
          const errorsText = Object.entries(errors).reduce(
            (text, [key, value]) => {
              let valueText = '';

              if (value && typeof value === 'object' && !Array.isArray(value)) {
                valueText = `<ul class="pl-2">${Object.entries(value)
                  .map(
                    ([nestedKey, nestedValue]) =>
                      `<li><strong>${nestedKey}</strong>: ${
                        Array.isArray(nestedValue)
                          ? nestedValue[0] +
                            (nestedValue.length > 1
                              ? ` (+${nestedValue.length - 1})`
                              : '')
                          : JSON.stringify(nestedValue, null, 2)
                      }</li>`,
                  )
                  .join('')}</ul>`;
              } else {
                valueText = Array.isArray(value)
                  ? value[0] +
                    (value.length > 1 ? ` (+${value.length - 1})` : '')
                  : JSON.stringify(value, null, 2);
              }

              return text + `<li><strong>${key}</strong>: ${valueText}</li>`;
            },
            '',
          );

          switch (errCount) {
            case 1:
              errCountText = `1 chyba`;
              break;
            case 2:
            case 3:
            case 4:
              errCountText = `${errCount} chyby`;
              break;
            case 5:
            default:
              `${errCount} chýb`;
              break;
          }

          toast({
            severity: 'danger',
            summary: '422: Chyba validácie',
            detail: `Skontrolujte zadané údaje (${errCountText})<ul>${errorsText}</ul>`,
            closable: true,
            onClose: () => {
              store.dispatch(ErrorHandlerActions.clearError({ error }));
            },
          });
        } else {
          toast({
            severity: 'danger',
            summary: '422: Chyba validácie',
            detail: error.error?.message || error.message,
            closable: true,
            onClose: () => {
              store.dispatch(ErrorHandlerActions.clearError({ error }));
            },
          });
        }
      }),
    );
  },
  { dispatch: false, functional: true },
);

export const redirectToForbidden$ = createEffect(
  (actions$ = inject(Actions), router = inject(Router)) => {
    return actions$.pipe(
      ofType(ErrorHandlerActions.setError),
      filter(({ error }) => error.status === 403),
      tap(() => router.navigate(['/forbidden'])),
    );
  },
  { functional: true, dispatch: false },
);
